/**
 * @fileoverview Input/output schemas for the prose-continuer-discovery agent.
 *
 * This agent generates exploratory prose continuation driven by character momentum,
 * world context, and narrative flow rather than a strict chapter plan.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Compared to prose-continuer-guided, the discovery variant foregrounds
 * characters and recent chapter content over structural chapter data.
 * Chapter context is still available as secondary context.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingGenreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.existingProse.schema)
  .merge(AGENT_SCHEMA_OBJECTS.lastThreeChaptersContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.writingStyleSample.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingHistory.schema)
  .merge(AGENT_SCHEMA_OBJECTS.isShortForm.schema)
  .merge(z.object({
    actualWordCount: z.number().optional().describe('Current word count of the existing prose.'),
    wordBudgetRemaining: z.number().optional().describe('Approximate words remaining before reaching the projected word count.'),
    isNearingWordBudget: z.boolean().optional().describe('True if the chapter is within 80-100% of its projected word count.'),
    isOverWordBudget: z.boolean().optional().describe('True if the chapter has exceeded its projected word count.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Unified output schema for content-producing agents.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining what emerged in your continuation — what character impulses or story momentum you followed (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The newly generated prose continuation, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
